import { store } from "../lib/store.js";
import { supabase } from "../lib/supabase.js";
import { approveQuest, rejectQuest } from "../lib/quest-actions.js";
import { pushToast } from "../lib/toasts.js";
import { formatRelativeTime } from "../utils/format.js";

const SEVERITIES = ["P0", "P1", "P2", "P3"];

export function mountQuestBoard(root) {
  root.innerHTML = `
    <section class="quest-board" aria-label="Quest Board — aprovações pendentes">
      <div class="quest-board-header">
        <button class="quest-board-toggle" aria-expanded="true" aria-controls="quest-list-region">📋 Pendências ▾</button>
        <h2 class="pixel-heading visually-hidden">Pendências</h2>
        <div class="quest-filters">
          <select data-filter="agent" aria-label="Filtrar por agente">
            <option value="">Todos os agentes</option>
          </select>
          <select data-filter="severity" aria-label="Filtrar por severidade">
            <option value="">Todas as severidades</option>
            ${SEVERITIES.map((s) => `<option value="${s}">${s}</option>`).join("")}
          </select>
          <select data-filter="reason" aria-label="Filtrar por tipo">
            <option value="">Todos os tipos</option>
          </select>
        </div>
      </div>
      <div class="quest-list" id="quest-list-region" role="list"></div>
    </section>

    <div class="reject-modal-backdrop" hidden></div>
    <div class="reject-modal" hidden role="dialog" aria-modal="true" aria-label="Motivo da rejeição">
      <h3>Rejeitar quest</h3>
      <label for="reject-reason">Motivo (obrigatório)</label>
      <textarea id="reject-reason" rows="3" required></textarea>
      <p class="reject-modal-error" role="alert" hidden></p>
      <div class="reject-modal-actions">
        <button class="reject-modal-cancel" type="button">Cancelar</button>
        <button class="reject-modal-confirm" type="button">Confirmar rejeição</button>
      </div>
    </div>

    <div class="detail-modal-backdrop" hidden></div>
    <div class="detail-modal" hidden role="dialog" aria-modal="true" aria-label="Detalhes da quest">
      <button class="detail-modal-close" type="button" aria-label="Fechar">×</button>
      <div class="detail-modal-content"></div>
    </div>
  `;

  const questList = root.querySelector(".quest-list");
  const agentFilter = root.querySelector('[data-filter="agent"]');
  const severityFilter = root.querySelector('[data-filter="severity"]');
  const reasonFilter = root.querySelector('[data-filter="reason"]');

  const rejectBackdrop = root.querySelector(".reject-modal-backdrop");
  const rejectModal = root.querySelector(".reject-modal");
  const rejectTextarea = root.querySelector("#reject-reason");
  const rejectError = root.querySelector(".reject-modal-error");
  let rejectTargetId = null;

  const detailBackdrop = root.querySelector(".detail-modal-backdrop");
  const detailModal = root.querySelector(".detail-modal");
  const detailContent = root.querySelector(".detail-modal-content");

  function closeDetailModal() {
    detailBackdrop.hidden = true;
    detailModal.hidden = true;
  }

  async function openDetailModal(questId) {
    detailBackdrop.hidden = false;
    detailModal.hidden = false;
    detailContent.innerHTML = `<p>Carregando...</p>`;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const response = await fetch(`/api/quest-detail?questId=${encodeURIComponent(questId)}`, {
        headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const { quest, order, customerFirstName } = await response.json();

      detailContent.innerHTML = `
        <h3>Quest #${quest.id.slice(0, 8)}</h3>
        <dl class="detail-modal-fields">
          <dt>Cliente</dt><dd>${escapeHtml(customerFirstName ?? "—")}</dd>
          <dt>Vertical</dt><dd>${escapeHtml(order?.vertical ?? "—")}</dd>
          <dt>Pacote</dt><dd>${escapeHtml(order?.chosen_package ?? "—")}</dd>
          <dt>Agente</dt><dd>${escapeHtml(quest.agent_name)}</dd>
          <dt>Motivo</dt><dd>${escapeHtml(quest.reason)}</dd>
          <dt>Severidade</dt><dd>${escapeHtml(quest.severity)}</dd>
        </dl>
      `;
    } catch (error) {
      detailContent.innerHTML = `<p class="detail-modal-error">Falha ao carregar detalhes: ${escapeHtml(error instanceof Error ? error.message : String(error))}</p>`;
    }
  }

  const knownAgents = new Set();
  const knownReasons = new Set();
  // order_id -> { vertical, customerFirstName } — cache do enriquecimento em
  // lote buscado via /api/quest-summaries (ver functions/api/quest-summaries.js).
  const summaryCache = new Map();
  let summaryFetchInFlight = false;

  async function ensureSummaries(orderIds) {
    const missing = orderIds.filter((id) => !summaryCache.has(id));
    if (missing.length === 0 || summaryFetchInFlight) return;
    summaryFetchInFlight = true;
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const response = await fetch("/api/quest-summaries", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
        body: JSON.stringify({ orderIds: missing }),
      });
      if (response.ok) {
        const { summaries } = await response.json();
        for (const [orderId, summary] of Object.entries(summaries ?? {})) {
          summaryCache.set(orderId, summary);
        }
        render(store.pendingQuests.get());
      }
    } catch (error) {
      console.error("Falha ao buscar resumo de quests", error);
    } finally {
      summaryFetchInFlight = false;
    }
  }

  function applyFilters(quests) {
    const agent = agentFilter.value;
    const severity = severityFilter.value;
    const reason = reasonFilter.value;
    return quests.filter(
      (q) => (!agent || q.agent_name === agent) && (!severity || q.severity === severity) && (!reason || q.reason === reason),
    );
  }

  function render(questsMap) {
    const quests = [...questsMap.values()].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    for (const q of quests) {
      knownAgents.add(q.agent_name);
      knownReasons.add(q.reason);
    }
    syncFilterOptions(agentFilter, knownAgents);
    syncFilterOptions(reasonFilter, knownReasons);

    const visible = applyFilters(quests);

    questList.innerHTML = visible.length
      ? visible.map((q) => questCardHtml(q, summaryCache.get(q.order_id))).join("")
      : `<p class="quest-list-empty">Nenhuma quest pendente.</p>`;

    ensureSummaries(quests.map((q) => q.order_id));
  }

  function syncFilterOptions(select, values) {
    const current = select.value;
    const existing = new Set([...select.options].map((o) => o.value));
    for (const value of values) {
      if (!existing.has(value)) {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      }
    }
    select.value = current;
  }

  function questCardHtml(q, summary) {
    const images = (q.context?.image_paths ?? []).slice(0, 9);
    const clienteVertical = summary
      ? `Cliente: ${escapeHtml(summary.customerFirstName ?? "—")} | Vertical: ${escapeHtml(summary.vertical ?? "—")}`
      : "Carregando cliente/vertical...";
    return `
      <article class="quest-card" role="listitem" tabindex="0" data-quest-id="${q.id}">
        <header class="quest-card-header">
          <span class="quest-card-id">🎯 Quest #${q.id.slice(0, 8)}</span>
          <span class="quest-card-severity quest-card-severity--${q.severity}">${q.severity}</span>
        </header>
        <p class="quest-card-summary">Aprovar imagens do pedido #${q.order_id.slice(0, 8)}</p>
        <p class="quest-card-cliente">${clienteVertical}</p>
        <p class="quest-card-meta">Agente: ${escapeHtml(q.agent_name)} · ${escapeHtml(q.reason)} · ${formatRelativeTime(q.created_at)}</p>
        ${
          images.length
            ? `<div class="quest-card-preview">${images.map((src) => `<img src="${src}" alt="" loading="lazy" />`).join("")}</div>`
            : ""
        }
        <div class="quest-card-actions">
          <button class="quest-approve" data-quest-id="${q.id}">✅ Aprovar</button>
          <button class="quest-reject" data-quest-id="${q.id}">❌ Rejeitar</button>
          <button class="quest-view" data-quest-id="${q.id}">🔍 Ver</button>
        </div>
      </article>
    `;
  }

  function openRejectModal(questId) {
    rejectTargetId = questId;
    rejectTextarea.value = "";
    rejectError.hidden = true;
    rejectBackdrop.hidden = false;
    rejectModal.hidden = false;
    rejectTextarea.focus();
  }

  function closeRejectModal() {
    rejectTargetId = null;
    rejectBackdrop.hidden = true;
    rejectModal.hidden = true;
  }

  root.addEventListener("click", async (event) => {
    const approveBtn = event.target.closest(".quest-approve");
    const rejectBtn = event.target.closest(".quest-reject");
    const viewBtn = event.target.closest(".quest-view");

    if (approveBtn) {
      const questId = approveBtn.dataset.questId;
      const card = root.querySelector(`.quest-card[data-quest-id="${questId}"]`);
      approveBtn.disabled = true;
      try {
        await approveQuest(questId);
        card?.classList.add("quest-card--resolved");
        pushToast("success", "Quest aprovada — entrega disparada.");
      } catch (error) {
        pushToast("error", error instanceof Error ? error.message : "Falha ao aprovar quest.");
        approveBtn.disabled = false;
      }
      return;
    }

    if (rejectBtn) {
      openRejectModal(rejectBtn.dataset.questId);
      return;
    }

    if (viewBtn) {
      openDetailModal(viewBtn.dataset.questId);
    }
  });

  root.querySelector(".reject-modal-cancel").addEventListener("click", closeRejectModal);
  rejectBackdrop.addEventListener("click", closeRejectModal);
  root.querySelector(".detail-modal-close").addEventListener("click", closeDetailModal);
  detailBackdrop.addEventListener("click", closeDetailModal);

  root.querySelector(".reject-modal-confirm").addEventListener("click", async () => {
    const reason = rejectTextarea.value.trim();
    if (!reason) {
      rejectError.textContent = "Motivo é obrigatório.";
      rejectError.hidden = false;
      return;
    }
    try {
      await rejectQuest(rejectTargetId, reason);
      pushToast("info", "Quest rejeitada.");
      closeRejectModal();
    } catch (error) {
      rejectError.textContent = error instanceof Error ? error.message : "Falha ao rejeitar.";
      rejectError.hidden = false;
    }
  });

  const toggleButton = root.querySelector(".quest-board-toggle");
  const questBoardSection = root.querySelector(".quest-board");
  toggleButton.addEventListener("click", () => {
    const collapsed = questBoardSection.classList.toggle("quest-board--collapsed");
    toggleButton.setAttribute("aria-expanded", String(!collapsed));
  });

  agentFilter.addEventListener("change", () => render(store.pendingQuests.get()));
  severityFilter.addEventListener("change", () => render(store.pendingQuests.get()));
  reasonFilter.addEventListener("change", () => render(store.pendingQuests.get()));

  let previousIds = new Set();
  const unsubscribe = store.pendingQuests.subscribe((questsMap) => {
    const currentIds = new Set(questsMap.keys());
    for (const id of currentIds) {
      if (!previousIds.has(id) && previousIds.size > 0) {
        pushToast("info", "Nova quest chegou no Quest Board.");
      }
    }
    previousIds = currentIds;
    render(questsMap);
  });

  return {
    focusFirstQuest() {
      root.querySelector(".quest-approve")?.closest(".quest-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
      root.querySelector(".quest-card")?.focus();
    },
    approveFocusedQuest() {
      const focused = root.querySelector(".quest-card:focus, .quest-card:hover");
      focused?.querySelector(".quest-approve")?.click();
    },
    rejectFocusedQuest() {
      const focused = root.querySelector(".quest-card:focus, .quest-card:hover");
      focused?.querySelector(".quest-reject")?.click();
    },
    closeModals() {
      closeRejectModal();
      closeDetailModal();
    },
    destroy: () => {
      unsubscribe();
      root.innerHTML = "";
    },
  };
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text ?? "";
  return div.innerHTML;
}
